import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSharedState } from "sharedStateProvider";
import PageLayout from "components/page-layout";
import Table, { tableHeader } from "components/tables/table";
import { oneLine } from "components/tables/table-parts";
import { getLocationUsers } from "api/location-monitoring";
import { LocationUsersListResponse, LocationUsersListRequest } from "types/location-users-type";
import { IconMapPin, IconClock, IconCheck, IconX, IconArrowLeft } from "@tabler/icons-react";

const PAGE_SIZE = 25;

// Helper function to format Unix timestamp (seconds) to readable date
const formatUnixTimestamp = (timestamp: number | null | undefined): string => {
  if (!timestamp) return "-";
  try {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  } catch {
    return "-";
  }
};

export default function LocationUsersList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify } = useSharedState();
  const [users, setUsers] = useState<LocationUsersListResponse>({ items: [], total: 0 });
  const [firstEntry, setFirstEntry] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isInitialMount = useRef(true);

  // Helper to parse boolean from URL param
  const parseBooleanParam = (value: string | null): boolean | undefined => {
    if (value === "true") return true;
    if (value === "false") return false;
    return undefined;
  };

  // Initialize filters from URL params
  const [filters, setFilters] = useState<LocationUsersListRequest>(() => {
    const initialFilters: LocationUsersListRequest = {
      limit: PAGE_SIZE,
      offset: 0,
    };
    
    initialFilters.inside_factory = parseBooleanParam(searchParams.get("inside_factory"));
    initialFilters.location_active = parseBooleanParam(searchParams.get("location_active"));
    initialFilters.waiting_location = parseBooleanParam(searchParams.get("waiting_location"));
    
    return initialFilters;
  });

  // Sync filters with URL params when they change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const newParams = new URLSearchParams();
    
    if (filters.inside_factory !== undefined) {
      newParams.set("inside_factory", filters.inside_factory.toString());
    }
    if (filters.location_active !== undefined) {
      newParams.set("location_active", filters.location_active.toString());
    }
    if (filters.waiting_location !== undefined) {
      newParams.set("waiting_location", filters.waiting_location.toString());
    }
    
    // Only update URL if params actually changed
    const currentString = searchParams.toString();
    const newString = newParams.toString();
    if (currentString !== newString) {
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.inside_factory, filters.location_active, filters.waiting_location]);

  // Load users
  const loadUsers = useCallback(async (page?: number) => {
    setIsLoading(true);
    const offset = page ? (page - 1) * PAGE_SIZE : 0;
    
    try {
      const params: LocationUsersListRequest = {
        ...filters,
        limit: PAGE_SIZE,
        offset,
      };
      
      const res: LocationUsersListResponse = await getLocationUsers(params);
      setUsers({
        items: Array.isArray(res?.items) ? res.items : [],
        total: typeof res?.total === "number" ? res.total : 0
      });
      setFirstEntry(offset);
    } catch (error: any) {
      const err = error?.response?.data?.error || "Ошибка при загрузке пользователей";
      notify(err, "danger", "Ошибка");
      setUsers({ items: [], total: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [filters, notify]);

  // Reload when filters change or on initial mount
  useEffect(() => {
    loadUsers();
  }, [filters.inside_factory, filters.location_active, filters.waiting_location, loadUsers]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterName: keyof LocationUsersListRequest, value: boolean | undefined) => {
    setFilters((prev) => {
      const newFilters: LocationUsersListRequest = { ...prev, offset: 0 };
      if (value === undefined) {
        const { [filterName]: _, ...rest } = newFilters;
        return rest;
      } else {
        return { ...newFilters, [filterName]: value };
      }
    });
  }, []);

  const clearFilters = useCallback(async () => {
    setFilters({ limit: PAGE_SIZE, offset: 0 });
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Table headers
  const usersTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: true },
    { header_text: "User ID", narrow: false, wrap: true },
    { header_text: "PERNR", narrow: false, wrap: true },
    { header_text: "Внутри завода", narrow: true, wrap: true },
    { header_text: "Отслеживание", narrow: true, wrap: true },
    { header_text: "Ожидает отслеживания", narrow: true, wrap: true },
    { header_text: "Создано", narrow: false, wrap: true },
    { header_text: "Обновлено", narrow: false, wrap: true },
  ];

  const usersList = (
    <>
      {(users.items || []).map((user, index) => (
        <tr key={`location-user-${user.user_id}-${index}`}>
          <td>{oneLine((firstEntry + index + 1).toString())}</td>
          <td>
            {user.user_id ? (
              <a 
                href={`/user/${user.user_id}`} 
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/user/${user.user_id}`);
                }}
                style={{ textDecoration: "none", color: "inherit" }}
                onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.currentTarget.style.textDecoration = "underline";
                  e.currentTarget.style.color = "var(--tblr-primary)";
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.currentTarget.style.textDecoration = "none";
                  e.currentTarget.style.color = "inherit";
                }}
              >
                {user.user_id}
              </a>
            ) : (
              oneLine("-")
            )}
          </td>
          <td>{oneLine(user.pernr || "-")}</td>
          <td>
            {user.is_employee_entered ? (
              <span className="badge bg-success">
                <IconCheck className="icon" /> Да
              </span>
            ) : (
              <span className="badge bg-secondary">
                <IconX className="icon" /> Нет
              </span>
            )}
          </td>
          <td>
            {user.is_tracking_on ? (
              <span className="badge bg-success">
                <IconMapPin className="icon" /> Активно
              </span>
            ) : (
              <span className="badge bg-danger">
                <IconMapPin className="icon" /> Неактивно
              </span>
            )}
          </td>
          <td>
            {user.tracking_start_requested_at ? (
              <span className="badge bg-warning">
                <IconClock className="icon" /> Ожидает
              </span>
            ) : (
              <span className="badge bg-secondary">-</span>
            )}
          </td>
          <td>{oneLine(formatUnixTimestamp(user.created_at || null))}</td>
          <td>{oneLine(formatUnixTimestamp(user.updated_at || null))}</td>
        </tr>
      ))}
    </>
  );

  const usersTable = Table(
    usersTableHeaders,
    usersList,
    true,
    {
      data: users.items || [],
      page: Math.max(1, Math.floor(firstEntry / PAGE_SIZE) + 1),
      size: PAGE_SIZE,
      total: users.total || 0,
      sort: ""
    },
    setFirstEntry,
    loadUsers
  );

  // Custom filters component
  const filtersComponent = (
    <div>
      <div className="mb-3">
        <button 
          className="btn btn-outline-secondary" 
          onClick={() => navigate("/location-monitoring")}
        >
          <IconArrowLeft className="icon" />
          Назад к мониторингу
        </button>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Внутри завода</label>
            <select
              className="form-select"
              value={filters.inside_factory === undefined ? "" : filters.inside_factory ? "true" : "false"}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : e.target.value === "true";
                handleFilterChange("inside_factory", value);
              }}
            >
              <option value="">Все</option>
              <option value="true">Да</option>
              <option value="false">Нет</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Отслеживание активно</label>
            <select
              className="form-select"
              value={filters.location_active === undefined ? "" : filters.location_active ? "true" : "false"}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : e.target.value === "true";
                handleFilterChange("location_active", value);
              }}
            >
              <option value="">Все</option>
              <option value="true">Активно</option>
              <option value="false">Неактивно</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Ожидает отслеживания</label>
            <select
              className="form-select"
              value={filters.waiting_location === undefined ? "" : filters.waiting_location ? "true" : "false"}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : e.target.value === "true";
                handleFilterChange("waiting_location", value);
              }}
            >
              <option value="">Все</option>
              <option value="true">Ожидает</option>
              <option value="false">Не ожидает</option>
            </select>
          </div>
        </div>
        {(filters.inside_factory !== undefined || filters.location_active !== undefined || filters.waiting_location !== undefined) && (
          <div className="mt-3">
            <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
              Очистить фильтры
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageLayout
        customHeader={filtersComponent}
        children={usersTable}
        isLoading={isLoading}
        filters={false}
        search={false}
        header="Позиционирование - Список пользователей"
        subheaderText="Количество пользователей: "
        subheaderCount={users.total}
        dataLength={users.items.length}
        dateRange={false}
      />
    </>
  );
}
